import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer'
import { BLOCKS } from '@contentful/rich-text-types'
import { motion, AnimatePresence } from 'framer-motion'
import { MdCheck } from 'react-icons/md'

import { Button } from '@/components/atoms/ui/button'

import { cn } from '@/lib/utils'

interface CorporateCardProps {
  title: string
  subTitle: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  description?: any
  estimate?: string | null
  buttonLink?: string | null
  buttonText?: string | null
  badgeText?: string | null
  badgeIcon?: string | null
  badgeColor?: string | null
  startFrom?: boolean | null
  price?: string | null
  priceUnit?: string | null
  specialPrice?: string | null
  serviceCategory?: string | null
  note?: string | null
  isEven?: boolean
}

export const CorporateCard = ({
  title,
  subTitle,
  description,
  price,
  priceUnit,
  note,
  buttonText,
  buttonLink,
  isEven,
  startFrom = false,
  specialPrice
}: CorporateCardProps) => {
  const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  }

  const renderOptions: Options = {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <motion.p>{children}</motion.p>,
      [BLOCKS.UL_LIST]: (_, children) => (
        <motion.ul className="space-y-2" variants={textVariants}>
          {children}
        </motion.ul>
      ),
      [BLOCKS.LIST_ITEM]: (_, children) => (
        <motion.li className="flex items-center text-sm lg:text-base gap-x-2">
          <span className={cn('flex items-center justify-center rounded-full size-7 bg-blue-50 text-primary-blue')}>
            <MdCheck size={18} />
          </span>
          <span className="w-[90%]">{children}</span>
        </motion.li>
      )
    }
  }

  return (
    <motion.figure
      className={cn(
        'p-6 lg:p-10 space-y-4 lg:space-y-5 border rounded-xl border-gray-300',
        isEven ? 'text-primary-blue bg-slate-50' : 'text-white bg-primary-blue'
      )}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={textVariants}
    >
      <header className="space-y-2 lg:space-y-3">
        <motion.h3 className="text-xl font-bold lg:text-2xl" variants={textVariants}>
          {title}
        </motion.h3>
        <motion.p className="font-bold lg:text-lg" variants={textVariants}>
          {subTitle}
        </motion.p>
        <motion.div variants={textVariants}>
          {startFrom && <span className="font-medium">Mulai dari</span>}
          <p className={cn('flex items-center gap-1 text-2xl font-extrabold lg:text-4xl')}>
            <div className="space-x-2">
              <span className={cn(specialPrice && (isEven ? 'line-through text-gray-500' : 'line-through text-gray-300'))}>
                {price}
              </span>
              {specialPrice && <span>{specialPrice}</span>}
            </div>
            <span className="text-sm font-normal lg:text-base">/ {priceUnit}</span>
          </p>
        </motion.div>
      </header>
      <Button
        url={buttonLink || '/hubungi-kami-via-whatsapp'}
        variant="secondary"
        size="lg"
        className="w-full font-semibold shadow gtm-call-button"
        target="_blank"
      >
        {buttonText || 'Berlangganan'}
      </Button>

      <AnimatePresence>
        <div className="[&>p:first-child]:text-lg [&>p:first-child]:lg:text-xl space-y-3">
          {documentToReactComponents(description, renderOptions)}
        </div>
      </AnimatePresence>
      <motion.p className={cn('text-sm', isEven ? 'text-gray-600' : 'text-white')} variants={textVariants}>
        <i>{note || 'Harga tidak termasuk PPN 11%'}</i>
      </motion.p>
    </motion.figure>
  )
}
